<!--
    LLM chat designer (tab "Chat"). Every text the LLM wrote is rendered via {{ }} interpolation ONLY
    (never innerHTML) - capability matrix I-LLM4 / H9.
-->
<div class="flex flex-column gap-3 h-full" data-test="ui-ql-assist-chat">

    @switch (statusPhase) {

        @case ('loading') {
            <div class="flex flex-column gap-2" data-test="ui-ql-assist-status-loading">
                <p-skeleton height="2.5rem"></p-skeleton>
                <p-skeleton height="8rem"></p-skeleton>
                <p-skeleton height="4rem"></p-skeleton>
            </div>
        }

        @case ('error') {
            <p-message severity="error" styleClass="w-full" data-test="ui-ql-assist-status-error">
                <div class="flex flex-column gap-2">
                    <span>{{ statusErrorText }}</span>
                    <div>
                        <p-button label="Erneut prüfen" icon="fa-regular fa-rotate-right" size="small"
                                  severity="secondary" [outlined]="true" (onClick)="loadStatus()"></p-button>
                    </div>
                </div>
            </p-message>
        }

        @default {
            @if (!enabled) {
                <div class="flex flex-column align-items-center text-center gap-2 p-4 surface-50 border-1 surface-border border-round-lg"
                     data-test="ui-ql-assist-disabled">
                    <i class="fa-regular fa-robot text-3xl text-color-secondary"></i>
                    <span class="text-lg font-semibold">{{ notConfiguredText }}</span>
                    <span class="text-color-secondary" data-test="ui-ql-assist-disabled-reason">{{ disabledReasonText }}</span>
                    <span class="text-sm text-color-secondary">Der Builder bleibt vollständig nutzbar.</span>
                </div>
            } @else {

                <!-- PII hint + model -->
                <p-message severity="info" styleClass="w-full" data-test="ui-ql-assist-pii-hint">
                    <div class="flex flex-column gap-1">
                        <span>{{ piiHint }}</span>
                        <span class="text-sm" data-test="ui-ql-assist-model">
                            Modell: {{ status?.modelName || 'unbekannt' }}
                            @if (status?.remainingUserRequests != null) {
                                · noch {{ status?.remainingUserRequests }} Anfragen
                            }
                        </span>
                    </div>
                </p-message>

                <!-- MESSAGE LIST -->
                <div #messageList class="ui-ql-assist-chat-list flex flex-column gap-3" data-test="ui-ql-assist-messages">

                    @if (!messages.length) {
                        <div class="flex flex-column gap-2 p-3 surface-50 border-1 surface-border border-round-lg"
                             data-test="ui-ql-assist-empty">
                            <span class="font-medium">Beschreiben Sie das gewünschte Widget.</span>
                            <span class="text-sm text-color-secondary">
                                Der Entwurf wird sofort in den Builder und die Vorschau geladen – mit Änderungsliste und „Rückgängig“.
                                Zum Beispiel:
                            </span>
                            <div class="flex flex-wrap gap-2" data-test="ui-ql-assist-suggestions">
                                @for (suggestion of suggestions; track suggestion) {
                                    <p-button [label]="suggestion" size="small" severity="secondary"
                                              [rounded]="true" [outlined]="true"
                                              (onClick)="useSuggestion(suggestion)"></p-button>
                                }
                            </div>
                        </div>
                    }

                    @for (message of messages; track message.id) {

                        @switch (message.role) {

                            @case ('user') {
                                <div class="flex justify-content-end">
                                    <div class="ui-ql-assist-chat-text ui-ql-assist-chat-bubble p-2 px-3 border-round-lg bg-primary"
                                         data-test="ui-ql-assist-user-message">{{ message.text }}</div>
                                </div>
                            }

                            @case ('error') {
                                <p-message [severity]="message.error?.kind === 'cancelled' ? 'secondary' : 'error'"
                                           styleClass="w-full" data-test="ui-ql-assist-error">
                                    <div class="flex flex-column gap-2">
                                        <span [attr.data-test]="'ui-ql-assist-error-' + message.error?.kind">{{ message.text }}</span>
                                        @if (message.error?.violations?.length) {
                                            <ul class="m-0 pl-3 flex flex-column gap-1">
                                                @for (violation of message.error?.violations; track $index) {
                                                    <li>
                                                        {{ violationText(violation) }}
                                                        <span class="text-sm text-color-secondary ml-1">({{ violation.code }} · {{ violation.path }})</span>
                                                    </li>
                                                }
                                            </ul>
                                        }
                                        @if (message.error?.retryable && message.retryText) {
                                            <div>
                                                <p-button label="Wiederholen" icon="fa-regular fa-rotate-right" size="small"
                                                          severity="secondary" [outlined]="true" [disabled]="busy"
                                                          data-test="ui-ql-assist-retry"
                                                          (onClick)="retry(message)"></p-button>
                                            </div>
                                        }
                                    </div>
                                </p-message>
                            }

                            @default {
                                @let response = message.response;
                                <div class="flex flex-column gap-2 p-3 surface-0 border-1 surface-border border-round-lg"
                                     data-test="ui-ql-assist-assistant-message">

                                    <div class="flex align-items-center justify-content-between gap-2">
                                        <span class="flex align-items-center gap-2 font-medium">
                                            <i class="fa-regular fa-robot text-primary"></i>Assistent
                                        </span>
                                        @if (response?.status; as turnStatus) {
                                            @let badge = statusBadge(turnStatus);
                                            <p-tag [value]="badge.label"
                                                   [severity]="badge.severity"
                                                   [attr.data-test]="'ui-ql-assist-badge-' + turnStatus"></p-tag>
                                        }
                                    </div>

                                    <!-- explanation: PLAIN TEXT -->
                                    @if (response?.explanation) {
                                        <div class="ui-ql-assist-chat-text" data-test="ui-ql-assist-explanation">{{ response?.explanation }}</div>
                                    }

                                    @if (response?.status === 'needsClarification' && response?.clarificationQuestion) {
                                        <div class="ui-ql-assist-chat-text p-2 border-round-md bg-blue-50 text-blue-800"
                                             data-test="ui-ql-assist-question">
                                            <i class="fa-regular fa-circle-question mr-2"></i>{{ response?.clarificationQuestion }}
                                        </div>
                                    }

                                    @if (response?.assumptions?.length) {
                                        <div class="flex flex-column gap-1" data-test="ui-ql-assist-assumptions">
                                            <span class="text-sm font-semibold">Annahmen</span>
                                            <ul class="m-0 pl-3 flex flex-column gap-1 text-sm">
                                                @for (assumption of response?.assumptions; track $index) {
                                                    <li class="ui-ql-assist-chat-text">{{ assumption }}</li>
                                                }
                                            </ul>
                                        </div>
                                    }

                                    @if (response?.changes?.length && response?.status !== 'unsupported') {
                                        <div class="flex flex-column gap-1" data-test="ui-ql-assist-changes">
                                            <span class="text-sm font-semibold">Änderungen</span>
                                            <ul class="list-none m-0 p-0 flex flex-column gap-1 text-sm">
                                                @for (change of response?.changes; track $index) {
                                                    <li class="flex align-items-start gap-2">
                                                        <i [class]="changeIcon(change)" class="mt-1"></i>
                                                        <span class="ui-ql-assist-chat-text">{{ change.label }}</span>
                                                    </li>
                                                }
                                            </ul>
                                        </div>
                                    }

                                    @if (response?.status === 'rejected' && response?.validation?.violations?.length) {
                                        <p-message severity="error" styleClass="w-full" data-test="ui-ql-assist-violations">
                                            <div class="flex flex-column gap-2">
                                                <span class="font-semibold">Der Entwurf verstößt gegen die Regeln und wurde nicht geladen:</span>
                                                <ul class="m-0 pl-3 flex flex-column gap-1">
                                                    @for (violation of response?.validation?.violations; track $index) {
                                                        <li>
                                                            {{ violationText(violation) }}
                                                            <span class="text-sm text-color-secondary ml-1">({{ violation.code }} · {{ violation.path }})</span>
                                                        </li>
                                                    }
                                                </ul>
                                            </div>
                                        </p-message>
                                    }

                                    @if (message.error?.unmappable?.length) {
                                        <p-message severity="error" styleClass="w-full" data-test="ui-ql-assist-unmappable">
                                            <div class="flex flex-column gap-2">
                                                <span class="font-semibold">Der Entwurf kann im Builder nicht dargestellt werden und wurde nicht geladen:</span>
                                                <ul class="m-0 pl-3 flex flex-column gap-1">
                                                    @for (text of message.error?.unmappable; track $index) {
                                                        <li>{{ text }}</li>
                                                    }
                                                </ul>
                                            </div>
                                        </p-message>
                                    }

                                    @if (mode === 'playground' && response?.usage; as usage) {
                                        <span class="text-xs text-color-secondary" data-test="ui-ql-assist-usage">
                                            {{ usage.llmCalls }} KI-Aufrufe · {{ usage.inputTokens }} / {{ usage.outputTokens }} Tokens
                                            · {{ (usage.durationMs / 1000) | number:'1.1-1' }} s
                                            @if (usage.correctionApplied) { · mit Korrekturrunde }
                                        </span>
                                    }

                                    <!-- actions -->
                                    <div class="flex flex-wrap align-items-center gap-2">
                                        @if (message.applied && message.id === undoTargetId) {
                                            <span class="text-sm text-green-600">
                                                <i class="fa-solid fa-check mr-1"></i>In Builder und Vorschau geladen
                                            </span>
                                            <p-button label="Rückgängig" icon="fa-regular fa-arrow-rotate-left" size="small"
                                                      severity="secondary" [outlined]="true"
                                                      [disabled]="busy || !canUndo"
                                                      data-test="ui-ql-assist-undo"
                                                      (onClick)="undo(message)"></p-button>
                                        }
                                        @if (message.undone) {
                                            <span class="text-sm text-color-secondary" data-test="ui-ql-assist-undone">
                                                <i class="fa-regular fa-arrow-rotate-left mr-1"></i>Rückgängig gemacht
                                            </span>
                                        }
                                        @if (response?.status === 'rejected' && message.loadable && !message.applied && !message.undone) {
                                            <p-button label="Trotzdem in Builder laden" icon="fa-regular fa-arrow-right-to-bracket"
                                                      size="small" severity="secondary" [outlined]="true" [disabled]="busy"
                                                      pTooltip="Die Verstöße bleiben sichtbar, Speichern bleibt gesperrt."
                                                      data-test="ui-ql-assist-load-anyway"
                                                      (onClick)="loadRejected(message)"></p-button>
                                        }
                                        @if (response?.status === 'rejected' && response?.proposal && notExecutable(response)) {
                                            <span class="text-sm text-color-secondary" data-test="ui-ql-assist-not-executable">
                                                <i class="fa-regular fa-ban mr-1"></i>Der Entwurf ist nicht ausführbar und wird daher nicht zum Laden angeboten.
                                            </span>
                                        }
                                    </div>
                                </div>
                            }
                        }
                    }

                    @if (busy) {
                        <div class="flex align-items-center gap-2 p-2 text-color-secondary" data-test="ui-ql-assist-typing">
                            <span class="ui-ql-assist-chat-typing" aria-hidden="true">
                                <span></span><span></span><span></span>
                            </span>
                            <span>Entwurf wird erstellt …</span>
                        </div>
                    }
                </div>

                <!-- INPUT -->
                <div class="flex flex-column gap-2">
                    <textarea pTextarea class="w-full" rows="3"
                              [maxlength]="maxMessageLength"
                              [disabled]="busy"
                              [(ngModel)]="inputText"
                              (keydown)="onInputKeydown($event)"
                              placeholder="Widget beschreiben … (Enter sendet, Umschalt+Enter für eine neue Zeile)"
                              aria-label="Nachricht an den KI-Assistenten"
                              data-test="ui-ql-assist-input"></textarea>

                    <div class="flex flex-wrap align-items-center justify-content-between gap-2">
                        <div class="flex align-items-center gap-3">
                            <span class="text-sm text-color-secondary" data-test="ui-ql-assist-counter">
                                {{ inputText.length }} / {{ maxMessageLength }}
                            </span>
                            @if (startEntity) {
                                <div class="flex align-items-center gap-2">
                                    <p-checkbox inputId="uiQlAssistPin" [binary]="true" [disabled]="busy"
                                                [(ngModel)]="pinStartEntity"></p-checkbox>
                                    <label for="uiQlAssistPin" class="text-sm">Start-Entität beibehalten ({{ startEntity }})</label>
                                </div>
                            }
                        </div>
                        <div class="flex align-items-center gap-2">
                            @if (busy) {
                                <p-button label="Abbrechen" icon="fa-regular fa-xmark" size="small" severity="secondary"
                                          [outlined]="true" data-test="ui-ql-assist-cancel"
                                          (onClick)="cancel()"></p-button>
                            }
                            <p-button label="Senden" icon="fa-regular fa-paper-plane" size="small"
                                      [disabled]="!canSend" data-test="ui-ql-assist-send"
                                      (onClick)="send()"></p-button>
                        </div>
                    </div>
                </div>
            }
        }
    }
</div>
